import React, { useMemo } from "react";
import { SWUTItem } from "../../forms/custom-swut/swutItemForm";

import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";
import Conditional from "../../common/conditional";
import CustomGeneralPhotos from "../custom-general/custom-general-photos";
import IsometricDrawing from "../custom-utm/isometric-drawing";
import PID from "../custom-visual-inspection/pid";
import SWUTObservationSummary from "./swutObservationSummary";
import ExecutiveSummary from "../custom-visual-inspection/executive-summary";
import OverviewSheet from "../custom-general/overview-sheet";
import ShearWaveReport from "./shearWaveReport";

import withPDF from "../../hoc/withPDF";
import useCoverSheetOverview from "../../../hooks/useCoverSheetOverview";

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
    reportTitle: string;
    coverData: models.CustomFVIData;
    executiveSummary: string;
    projectDetails: models.Project;
    pidData: models.PIDData;
    drawing: models.PDFImageProps[];
    scanPlan: models.PDFImageProps[];
    swutReports: SWUTItem[];
    photoRecord: models.PDFImageProps[];
}

const SWUTReport: React.FC<Props> = ({ executiveSummary, pidData, projectDetails, coverData, reportTitle, photoRecord, drawing, scanPlan, swutReports }) => {
    const { inspectionTypeName } = projectDetails;

    const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

            <SWUTObservationSummary swutItems={swutReports} />

			<PID
				hasPID={pidData.hasPID}
				pidImages={pidData.pIDImages} 
				pidNumber={pidData.pIDNumber} 
				sectionTitle="P & ID" 
			/>

            <IsometricDrawing label="Drawing" isoImages={drawing} />
            
            <IsometricDrawing label="Scan Plan" isoImages={scanPlan} />

            <ShearWaveReport items={swutReports} />

            <Conditional isVisible={photoRecord.length > 0}>
                <CustomGeneralPhotos label="Inspection Findings" subLabel="Photographic Record" photos={photoRecord} />
            </Conditional>
        </React.Fragment>
	);
};
 
export default withPDF(SWUTReport);